import { RawDraftContentState } from 'draft-js';
import Handsontable from 'handsontable/base';
import { cloneDeep, get } from 'lodash';
import {
    CellData,
    CellDataChange,
    TableChangeSource,
    TableContentData,
} from '../../../../../../common/table/TableTypes';
import { createTableCellEntry } from '../../../../../../common/table/utils/tableInitialisationUtils';
import { getColumnCount, getRowCount } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { propIn } from '../../../../../../common/utils/immutableHelper';
import { createNewCellData } from '../../dataUtils/cell/tableCellEditingUtils';
import { isFormula } from '../../dataUtils/formula/tableFormulaPredicateUtils';

/**
 * Converts Handsontable cell changes into persisted cell model changes.
 */
export const createCellDataChanges = (
    changes: Array<Handsontable.CellChange>,
    prevTableData: TableContentData,
    locale: string,
    currencyPreference: string,
    tableEditorDraftContent?: RawDraftContentState,
): Array<CellDataChange> => {
    return changes.map((change) => {
        const [row, col, , newCellValue] = change;
        const prevCellData = get(prevTableData, [row, col as number]);
        const newCellData = createNewCellData(
            prevCellData,
            newCellValue,
            locale,
            currencyPreference,
            tableEditorDraftContent,
        );

        return [row, col as number, prevCellData, newCellData];
    });
};

/**
 * Rebuilds HOT undo/redo changes from pre-recorded Milanote cell data when available.
 */
export const createCellDataChangesFromReplay = (
    changes: Array<Handsontable.CellChange>,
    prevTableData: TableContentData,
    replayCellDataByCoordinate: Map<string, CellData>,
    locale: string,
    currencyPreference: string,
): Array<CellDataChange> =>
    changes.map((change) => {
        const [row, col, , newCellValue] = change;
        const numericCol = Number(col);
        const prevCellData = get(prevTableData, [row, numericCol]);
        const replayedCellData = replayCellDataByCoordinate.get(`${row}:${numericCol}`);

        const newCellData = replayedCellData
            ? cloneDeep(replayedCellData)
            : createNewCellData(prevCellData, newCellValue, locale, currencyPreference);

        return [row, numericCol, prevCellData, newCellData];
    });

/**
 * Converts copy-like Handsontable changes into persisted cell model changes using the copied source cell data.
 */
export const createCellDataChangesFromSource = (
    changes: Array<[number, number, any, any]>,
    prevTableData: TableContentData,
    source: string,
    sourceCellDataArray: TableContentData | undefined,
    locale: string,
) => {
    const minChangeRow = Math.min(...changes.map(([row]) => row));
    const minChangeCol = Math.min(...changes.map(([_, col]) => col));

    return changes.map((change) => {
        const [row, col, , newCellValue] = change;
        const prevCellData = propIn([row, col], prevTableData);

        if (!sourceCellDataArray) {
            return [row, col, prevCellData, createTableCellEntry(newCellValue, locale)];
        }

        const nRowsOfSource = getRowCount(sourceCellDataArray);
        const nColsOfSource = getColumnCount(sourceCellDataArray);
        const sourceRow = (row - minChangeRow) % nRowsOfSource;
        const sourceCol = (col - minChangeCol) % nColsOfSource;
        const sourceData = cloneDeep(sourceCellDataArray[sourceRow][sourceCol]);

        if (source === TableChangeSource.AUTOFILL_FILL && isFormula(sourceData.value)) {
            sourceData.value = newCellValue;
        }

        return [row, col, prevCellData, sourceData];
    });
};
